import { DateInput as ADateInput } from '@ark-ui/solid/date-input';
import { DateFormatter } from '@internationalized/date';
import { children, Show, splitProps } from 'solid-js';
import { cn } from 'tailwind-variants';
import { Field } from './field.tsx';
import type { ComponentProps, JSXElement } from 'solid-js';

const timeFormatter = new DateFormatter('en-US', {
	hour: 'numeric',
	minute: '2-digit',
	hourCycle: 'h12',
});

export function TimeInput(props: ComponentProps<typeof ADateInput.Root> & { name: string }) {
	return (
		<Field name={props.name}>
			<ADateInput.Root granularity="minute" formatter={timeFormatter} {...props}>
				{props.children}
			</ADateInput.Root>
		</Field>
	);
}

TimeInput.Label = (props: ComponentProps<typeof ADateInput.Label>) => (
	<ADateInput.Label {...props} class={cn('text-lg font-medium', props.class)} />
);

TimeInput.Control = (props: ComponentProps<typeof ADateInput.Control>) => {
	return (
		<>
			<ADateInput.Control
				{...props}
				class="relative flex w-full items-center justify-between gap-x-1 rounded-md border border-zinc-300 bg-white px-3 text-lg transition-all outline-none group-hover/field:ring-4 group-hover/field:ring-yellow-400/30 has-focus-visible:ring-4 has-focus-visible:ring-yellow-500/50 has-focus-visible:ring-offset-1 has-focus-visible:ring-offset-zinc-50 has-disabled:bg-zinc-100 has-disabled:text-zinc-500 has-aria-invalid:border-rose-600 has-aria-readonly:cursor-not-allowed has-aria-readonly:bg-zinc-100 has-aria-readonly:text-zinc-500 data-com-onepassword-filled:bg-clip-text motion-safe:has-disabled:active:animate-shake dark:border-zinc-700 dark:bg-black dark:has-focus-visible:ring-offset-zinc-950 dark:has-disabled:bg-zinc-800"
			>
				<ADateInput.SegmentGroup class="flex gap-px py-2 select-none *:inline-flex *:items-center *:justify-end *:rounded *:px-0.5 *:tabular-nums *:caret-transparent *:outline-none *:not-aria-hidden:min-w-[calc(2ch+0.25rem)] *:focus:bg-yellow-500 *:focus:text-zinc-950 *:data-placeholder-shown:text-zinc-500 *:focus:data-placeholder-shown:text-zinc-950 *:data-[type=f]:min-w-0 *:data-[type=f]:px-0 *:data-[type=literal]:text-zinc-500 *:data-[type=literal]:select-none">
					<ADateInput.SegmentContext>{(segment) => <ADateInput.Segment segment={segment} />}</ADateInput.SegmentContext>
				</ADateInput.SegmentGroup>
			</ADateInput.Control>
			<ADateInput.HiddenInput />
		</>
	);
};

export function TimeInputField(
	props: ComponentProps<typeof TimeInput.Control> & { name: string; label: JSXElement; description?: JSXElement },
) {
	const [other, rest] = splitProps(props, ['label', 'description']);
	const description = children(() => props.description);
	return (
		<TimeInput name={rest.name}>
			<TimeInput.Label>{other.label}</TimeInput.Label>
			<TimeInput.Control {...rest} />
			<Field.Errors />
			<Show when={description()}>{(desc) => <Field.Description>{desc()}</Field.Description>}</Show>
		</TimeInput>
	);
}
